import React, { useRef, useEffect, useContext, useState } from 'react';
import { ChevronDown, Layers, PlusSquare, Database, X, Settings, Lock, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import Tooltip from '../Tooltip';
import { useSpaceToolsStore } from './stores/spaceToolsStore';
import { CadviewerContext } from '../../context/Cadviewer.Context';
import cadviewer from 'cadviewer';
import useConfig from '../../demos/hooks/useConfig';
import { useRoleProfileStore, type ToolboxKey } from '../../modules/roleProfile/roleProfileStore';

// Tools Icon Component
const ToolsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

// Report Icon
const ReportIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// Chart Icon
const ChartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </svg>
);

// Text Manipulation Icon
const TextManipulationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <polyline points="4 7 4 4 20 4 20 7" />
    <line x1="9" y1="20" x2="15" y2="20" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

// Data Import Icon
const DataImportIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
    />
  </svg>
);

/**
 * Space Tools wears the site's skin.
 *
 * The menu was painted white whatever `setCADViewerSkin` said, so a site set
 * to a dark skin opened a white panel over a dark drawing — the one surface
 * that ignored the choice its owner had made. The headers and icons follow it
 * now; what a site may still colour itself is its buttons and its left pane,
 * not this.
 */
const DARK_SKINS = new Set(['dark-skin', 'dark.skin', 'black', 'deepblue']);

interface SpaceToolsSkin {
  dark: boolean;
  panel: string;
  strip: string;
  divider: string;
  border: string;
  rowHover: string;
  title: string;
  body: string;
  subtle: string;
  faint: string;
}

function spaceToolsSkin(setCADViewerSkin?: unknown): SpaceToolsSkin {
  const dark = DARK_SKINS.has(String(setCADViewerSkin ?? '').toLowerCase());
  return dark
    ? {
        dark: true,
        panel: 'bg-neutral-900',
        strip: 'bg-neutral-800',
        divider: 'bg-neutral-700',
        border: 'border-neutral-700',
        rowHover: 'hover:bg-neutral-800',
        title: 'text-neutral-100',
        body: 'text-neutral-200',
        subtle: 'text-neutral-400',
        faint: 'text-neutral-500',
      }
    : {
        dark: false,
        panel: 'bg-white',
        strip: 'bg-gray-50',
        divider: 'bg-gray-200',
        border: 'border-gray-200',
        rowHover: 'hover:bg-gray-50',
        title: 'text-gray-900',
        body: 'text-gray-700',
        subtle: 'text-gray-500',
        faint: 'text-gray-400',
      };
}

interface SpaceToolsButtonProps {
  position?: 'bottom-right' | 'bottom-left';
}

const SpaceToolsButton: React.FC<SpaceToolsButtonProps> = ({ position = 'bottom-right' }) => {
  const { menuOpen, toggleMenu, setActivePanel, setMenuOpen, selectedLayers, activePanel } = useSpaceToolsStore();
  const { isDataImportActive, setIsDataImportActive, setCopiedSpaceId, currentDrawingPath, currentDrawingVersion, user } = useContext(CadviewerContext);
  /**
   * What this person's role is shown. A site that says nothing about their
   * role shows everything, exactly as it did before profiles existed — and
   * what is hidden here is still guarded wherever it is acted on.
   */
  const toolbox = useRoleProfileStore((state) => state.toolbox);
  const canSee = (key: ToolboxKey) =>
    Object.keys(toolbox).length === 0 || toolbox[key] !== false;
  const { config } = useConfig();
  // Read once per render from the same config that drives the rest of the
  // viewer's skin, so the menu can never disagree with the page behind it.
  const skin = spaceToolsSkin((config as { setCADViewerSkin?: unknown }).setCADViewerSkin);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showLoginWarning, setShowLoginWarning] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuOpen || !menuRef.current) return;
      
      const clickedInsideMenu = menuRef.current.contains(event.target as Node);
      const clickedInsideButton = buttonRef.current?.contains(event.target as Node);
      
      if (!clickedInsideMenu && !clickedInsideButton) {
        setMenuOpen(false);
        setExpandedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, setMenuOpen]);

  // Menu positions down from the button
  const menuPosition = position === 'bottom-right'
    ? 'right-0 top-0'
    : 'left-0 top-0';

  const handleTextManipulationClick = () => {
    setActivePanel('textManipulation');
  };

  return (
    <div className="relative z-50">
      {/* Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${menuPosition} w-64`}
          >
            <div className={`${skin.panel} rounded-lg shadow-xl border ${skin.border} overflow-hidden flex flex-col`}>
              
              {/* Header / Active Button */}
              <button 
                onClick={toggleMenu}
                className={`relative z-[60] w-full px-4 py-3 bg-btn-fab text-btn-fab-text flex items-center justify-between hover:brightness-110 transition-all border-b ${skin.border} cursor-pointer`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <ToolsIcon />
                  </div>
                  <span className="font-semibold text-sm">Space Tools</span>
                </div>
                <X size={18} />
              </button>

              <div className="relative flex-1 flex flex-col">
              {/* Current File & Version Pane */}
              {(currentDrawingPath) && (
                <div className={`px-4 py-3 ${skin.strip} border-b ${skin.border} text-xs`}>
                  <p className={`${skin.subtle} font-medium mb-1`}>Loaded File</p>
                  <div className="overflow-x-auto whitespace-nowrap pb-1" style={{ scrollbarWidth: 'thin' }}>
                    <p className={`${skin.title} font-semibold inline-block`} title={
                      (() => {
                        try { return decodeURIComponent(currentDrawingPath.split(/[/\\]/).pop() || ""); }
                        catch(e) { return currentDrawingPath.split(/[/\\]/).pop(); }
                      })()
                    }>
                      {(() => {
                        let fileName = currentDrawingPath.split(/[/\\]/).pop() || "";
                        try { fileName = decodeURIComponent(fileName); } catch(e) {}
                        const ext = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
                        if (ext) {
                          const firstExtIndex = fileName.toLowerCase().indexOf(ext.toLowerCase());
                          if (firstExtIndex !== -1) {
                            return fileName.substring(0, firstExtIndex + ext.length);
                          }
                        }
                        return fileName;
                      })()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`${skin.subtle} font-medium`}>Version:</span>
                    {(() => {
                      const isLatest = currentDrawingVersion === 'LATEST_FROM_TREE' || !currentDrawingVersion;
                      return (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          isLatest ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {isLatest ? 'Current' : (currentDrawingVersion === 'Current' ? 'Initial' : currentDrawingVersion)}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Layer List Option */}
              {canSee('layerList') && (<>
              <button
                onClick={() => { setActivePanel('layerList'); setExpandedCategory(null); }}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Layers size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>Layer List</p>
                    <p className={`text-xs ${skin.subtle}`}>View and manage layers</p>
                  </div>
                </div>
              </button>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Space Creation Option */}
              {canSee('spaceCreation') && (<>
              <button
                onClick={() => setExpandedCategory(expandedCategory === 'creation' ? null : 'creation')}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <PlusSquare size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>Space Creation</p>
                    <p className={`text-xs ${skin.subtle}`}>Create new spaces</p>
                  </div>
                </div>
                <ChevronDown size={16} className={`${skin.faint} transition-transform ${expandedCategory === 'creation' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {expandedCategory === 'creation' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-slate-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => setActivePanel('processSourceDWG')}
                        className={`w-full pl-16 pr-4 py-2 text-sm transition-colors text-left flex flex-col ${activePanel === 'processSourceDWG' ? 'bg-indigo-50 text-indigo-700 font-semibold border-r-2 border-indigo-600' : `${skin.body} hover:bg-slate-100 hover:text-indigo-600`}`}
                      >
                        <span className="font-medium">Process Source DWG</span>
                      </button>
                      <button
                        onClick={() => setActivePanel('jsonDataExtraction')}
                        className={`w-full pl-16 pr-4 py-2 text-sm transition-colors text-left flex flex-col ${activePanel === 'jsonDataExtraction' ? 'bg-indigo-50 text-indigo-700 font-semibold border-r-2 border-indigo-600' : `${skin.body} hover:bg-slate-100 hover:text-indigo-600`}`}
                      >
                        <span className="font-medium">JSON Data Extraction</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Space Association Option */}
              {canSee('spaceAssociation') && (<>
              <button
                onClick={() => setExpandedCategory(expandedCategory === 'association' ? null : 'association')}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Database size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>Space Association</p>
                    <p className={`text-xs ${skin.subtle}`}>Link spaces to external data</p>
                  </div>
                </div>
                <ChevronDown size={16} className={`${skin.faint} transition-transform ${expandedCategory === 'association' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {expandedCategory === 'association' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-slate-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => setActivePanel('spaceAssociation')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Associate space with Database
                      </button>
                        <button
                          onClick={() => setActivePanel('informationLayersSetup')}
                          className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                        >
                          Configure Information Layers
                        </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* VizQuery Setup Option */}
              {canSee('vizQuerySetup') && (<>
              <button
                onClick={() => setExpandedCategory(expandedCategory === 'vizquery' ? null : 'vizquery')}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Settings size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>VizQuery Setup</p>
                    <p className={`text-xs ${skin.subtle}`}>Configure Visual Queries</p>
                  </div>
                </div>
                <ChevronDown size={16} className={`${skin.faint} transition-transform ${expandedCategory === 'vizquery' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {expandedCategory === 'vizquery' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-slate-50"
                  >
                    <div className="py-1">
{canSee('folderStructure') && (
                      <button
                        onClick={() => setActivePanel('vizQuerySetup')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Folder Structure
                      </button>
                      )}
{canSee('applicationSetup') && (
                      <button
                        onClick={() => setActivePanel('applicationSetup')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Application Setup
                      </button>
                      )}
{canSee('aiSetup') && (
                      <button
                        onClick={() => setActivePanel('aiSetup')}
                        className="w-full pl-16 pr-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 hover:text-orange-600 transition-colors text-left"
                      >
                        AI Setup
                      </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Space Reporting Option */}
              {canSee('spaceReporting') && (<>
              <button
                onClick={() => setExpandedCategory(expandedCategory === 'reporting' ? null : 'reporting')}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ReportIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>Space Reporting</p>
                    <p className={`text-xs ${skin.subtle}`}>Select spaces for reporting</p>
                  </div>
                </div>
                <ChevronDown size={16} className={`${skin.faint} transition-transform ${expandedCategory === 'reporting' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {expandedCategory === 'reporting' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-slate-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => setActivePanel('lod')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Level of Detail (LOD)
                      </button>
                      <button
                        onClick={() => setActivePanel('svgStyling')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        SVG Styling
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Space Analytics Option */}
              {canSee('spaceAnalytics') && (<>
              <button
                onClick={() => setExpandedCategory(expandedCategory === 'analytics' ? null : 'analytics')}
                className={`w-full px-4 py-3 flex items-center justify-between ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ChartIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${skin.title}`}>Space Analytics</p>
                    <p className={`text-xs ${skin.subtle}`}>Financial & Opportunity Analysis</p>
                  </div>
                </div>
                <ChevronDown size={16} className={`${skin.faint} transition-transform ${expandedCategory === 'analytics' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {expandedCategory === 'analytics' && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden bg-slate-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => setActivePanel('financialAnalysis')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Financial Analysis
                      </button>
                      <button
                        onClick={() => setActivePanel('opportunityCost')}
                        className={`w-full pl-16 pr-4 py-2 text-sm ${skin.body} hover:bg-slate-100 hover:text-indigo-600 transition-colors text-left`}
                      >
                        Opportunity Cost
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Text Manipulation Option */}
              {canSee('textManipulation') && (<>
              <button
                onClick={handleTextManipulationClick}
                className={`w-full px-4 py-3 flex items-center gap-3 ${skin.rowHover} transition-colors text-left group`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                  <TextManipulationIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${skin.title}`}>Text Manipulation</p>
                  <p className={`text-xs ${skin.subtle}`}>Edit text properties</p>
                </div>
              </button>

              {/* Divider */}
              <div className={`h-px ${skin.divider}`} />
              </>)}

              {/* Toggle Data Import Option */}
              {canSee('dataImport') && (<>
              <button
                onClick={() => {
                  const newState = !isDataImportActive;
                  setIsDataImportActive(newState);
                  if (!newState) {
                    setCopiedSpaceId(null);
                  }
                  cadviewer.cvjs_hideOnlyPop();
                  setMenuOpen(false);
                }}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left group ${
                  isDataImportActive ? 'bg-indigo-50 hover:bg-indigo-100' : `${skin.rowHover}`
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform ${
                  isDataImportActive ? 'bg-indigo-600 text-white' : 'bg-btn-fab text-btn-fab-text'
                }`}>
                  <DataImportIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isDataImportActive ? 'text-indigo-900' : `${skin.title}`}`}>Toggle Data Import</p>
                  <p className={`text-xs ${isDataImportActive ? 'text-indigo-600' : `${skin.subtle}`}`}>Associate Spaces with Data Sources</p>
                </div>
              </button>
              </>)}
            </div>

                {config.enableLoginFeature && (!user || !user.token) && (
                  <div 
                    className={`absolute inset-0 ${skin.panel}/30 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center cursor-pointer group`}
                    onClick={() => setShowLoginWarning(true)}
                  >
                    <div className={`${skin.panel} p-3 rounded-full shadow-md group-hover:scale-110 transition-transform`}>
                      <Lock className="w-6 h-6 text-slate-500" />
                    </div>
                    <span className={`mt-3 text-sm font-semibold text-slate-700 ${skin.panel}/90 px-3 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity`}>
                      Login Required
                    </span>
                  </div>
                )}
              </div>
          </motion.div>
        )}
      </AnimatePresence>


        {/* Login Warning Modal */}
        <AnimatePresence>
          {showLoginWarning && (
            <div className="fixed top-20 inset-x-0 bottom-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className={`${skin.panel} rounded-xl shadow-2xl max-w-sm w-full overflow-hidden`}
              >
                <div className="px-6 py-5 flex items-center gap-4 bg-slate-50 border-b">
                  <div className="bg-amber-100 p-2 rounded-full text-amber-600">
                    <AlertCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Login Required</h3>
                    <p className="text-sm text-slate-500">Access to Space Tools is restricted.</p>
                  </div>
                </div>
                <div className="px-6 py-5">
                  <p className="text-sm text-slate-700">
                    Please log in or register for an account using the buttons in the top right corner of the application to unlock these features.
                  </p>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t flex justify-end">
                  <button
                    onClick={() => setShowLoginWarning(false)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Got it
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      {/* Main Button */}
      {!menuOpen && (
        <Tooltip text="Space Creation and Interaction Tools" className={position === 'bottom-right' ? '!left-1/2 !-translate-x-full' : ''}>
          <button
            ref={buttonRef}
            onClick={toggleMenu}
            className={`p-3 bg-btn-fab text-btn-fab-text rounded-full shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-btn-fab-ring focus:ring-offset-2 transition-transform duration-150 ease-in-out`}
            aria-label="Open Space Tools Menu"
            title="Space & Text Tools"
          >
            <ToolsIcon />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default SpaceToolsButton;
